
import {
    Flex,
    FormControl,
    FormErrorMessage,
    Input,
    Stack,
    Button,
    InputGroup,
    InputLeftElement,
    Text,
    useColorModeValue,
    Link,
    Image,
} from '@chakra-ui/react'
import { 
    useUserForgotPasswordSendEmailOtpMutation, 
    useUserForgetPasswordVerifyEmailMutation,
    useCheckIfEmailExistsMutation
} from '../../slices/userApiSlice';
import { ToastService } from '../../services/toast/toastService';
import { useNavigate } from "react-router";
import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAt } from "@fortawesome/free-solid-svg-icons"; 

const UserForgetPassword = () => {
  const [email, setEmail] = useState('');
  const {success, error} = ToastService();
  const [otp, setOTP] = useState(""); 
  const [resendDisabled, setResendDisabled] = useState(false); 
  const [resendTimer, setResendTimer] = useState(5 * 60); // 10 minutes timer
  const [expirationTime, setExpirationTime] = useState(null); // Store expiration time
  const [formErrors, setFormErrors] = useState();
  const navigate = useNavigate();
  const bgColor = useColorModeValue('custom.900', 'gray.800');
  const bgColorDark = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('custom.800', 'custom.900');
  const [userForgotPasswordSendEmailOtp] = useUserForgotPasswordSendEmailOtpMutation();
  const [isSendOtpLoading, setIsSendOtpLoading] = useState(false);
  const [verifyEmail] = useUserForgetPasswordVerifyEmailMutation();
  const [isVerifyEmailLoading, setIsVerifyEmailLoading] = useState(false);
  const [checkEmail] = useCheckIfEmailExistsMutation();
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  useEffect(() => {
  
    // Load reCAPTCHA v3 script
    const loadRecaptcha = () => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setRecaptchaLoaded(true);
      };
      document.body.appendChild(script);
    };
  
    if (!window.grecaptcha) {
      loadRecaptcha();
    } else {
      setRecaptchaLoaded(true);
    }
  
    return () => {
      const script = document.querySelector(`script[src^="https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}"]`);
      if (script) document.body.removeChild(script);
    };
  }, []);

  const getRecaptchaToken = async (action) => {
    try {
      // Wait for grecaptcha to be loaded
      await new Promise((resolve) => {
        const checkRecaptcha = () => {
          if (window.grecaptcha && window.grecaptcha.execute) {
            resolve();
          } else {
            setTimeout(checkRecaptcha, 100);
          }
        };
        checkRecaptcha();
      });

      const token = await window.grecaptcha.execute(
        process.env.REACT_APP_RECAPTCHA_SITE_KEY, 
        { action }
      );
      return token;
    } catch (err) {
      //console.error('reCAPTCHA error:', err);
      //throw new Error('Failed to verify reCAPTCHA');
    }
  };

  const validateForm = (excludeOTP = false) => {
    let errors = {};
    if (!email) {
      errors.email = "Please enter your email address.";
    }
    if (!excludeOTP && !otp) {
      errors.otp = "Verification code is required.";
    }
    return errors;
  };

  // Function to request OTP
  const sendOTP = async () => {
    // Validate form
    const validationErrors = validateForm(true);
    if (Object.keys(validationErrors).length > 0) {
        setFormErrors((prevErrors) => ({
            ...prevErrors,
            ...validationErrors,
        }));
        return;
    }
    setIsSendOtpLoading(true);

    try {
      await checkEmail({email}).unwrap();
      
      //const response = await axios.post('/api/forgot-password', { email });
      const token = await getRecaptchaToken('sendOTP');
      const response = await userForgotPasswordSendEmailOtp({ email, recaptchaToken: token }).unwrap();
      if (response.success) {
        // Set new expiration time (5 minutes from now)
        const newExpirationTime = Date.now() + 5 * 60 * 1000; // 5 minutes in milliseconds
        setExpirationTime(newExpirationTime);
  
        // Reset resend timer and disable the resend button
        setResendDisabled(true);
        setResendTimer(5 * 60); // Reset timer to 5 minutes
  
        success('A verification code has been sent to your email.');
      } else {
        error('Please check your email address');
      }
      
    } catch (err) {
        error(err.data.message || 'Failed to send OTP. Please try again.');
    } finally {
        // Ensure loading state is reset
        setIsSendOtpLoading(false);
    }
  };

  useEffect(() => {
    if (expirationTime) {
        const interval = setInterval(() => {
            const remainingTime = Math.max(0, Math.floor((expirationTime - Date.now()) / 1000)); // Calculate remaining time in seconds
            setResendTimer(remainingTime);

            if (remainingTime <= 0) {
                setResendDisabled(false); // Re-enable resend button after OTP expiration
                clearInterval(interval);
            }
        }, 1000);

        return () => clearInterval(interval); // Cleanup interval on component unmount
    }
}, [expirationTime]);
  
    const formatTime = (timeInSeconds) => {
      const minutes = Math.floor(timeInSeconds / 60); // Get minutes
      const seconds = timeInSeconds % 60; // Get remaining seconds
      return `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`; // Format as MM:SS
    };

    // OTP input change handler
    const handleOTPChange = (e) => {
        const value = e.target.value;

        // Update name state
        setOTP(value);

        // Clear error message if input value changes
        setFormErrors((prevErrors) => ({
            ...prevErrors,
            otp: "", // Clear the error for the name field
        }));
    };

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);
    
        // Clear email error as the user types
        if (formErrors?.email) {
            setFormErrors((prevErrors) => ({
                ...prevErrors,
                email: '',
            }));
        }
    };

    const handleEmailBlur = () => {
        if (!email) return; // If email is empty, do nothing
      
        // Email domain check (Only allow Gmail)
        const emailDomain = email.split('@')[1];
        if (emailDomain && emailDomain !== 'gmail.com') {
          setFormErrors((prevErrors) => ({
            ...prevErrors,
            email: 'Only Gmail addresses are supported.',
          }));
          return;
        }
      
        // Validate email using regex
        const emailRegex = /^[a-zA-Z0-9._-]+@gmail\.com$/; // Ensure only Gmail is allowed
        const isValid = emailRegex.test(email);
      
        setFormErrors((prevErrors) => ({
          ...prevErrors,
          email: isValid ? '' : 'Email is invalid.',
        }));
    };

    const submitHandler = async (e) => {
        e.preventDefault();
        setIsVerifyEmailLoading(true);
    
        // Step 1: Validate the form
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setFormErrors((prevErrors) => ({
                ...prevErrors,
                ...validationErrors,
            }));
            setIsVerifyEmailLoading(false);
            return; // Exit if validation fails
        }
    
        // Step 2: Check if OTP has expired (only if expirationTime is set)
        if (expirationTime && Date.now() > expirationTime) {
            setFormErrors((prevErrors) => ({
                ...prevErrors,
                otp: 'OTP has expired. Please request a new OTP.',
            }));
            setIsVerifyEmailLoading(false);
            return; // Exit if OTP has expired
        }
    
        try {
            if (!recaptchaLoaded) {
              error("Security verification is still loading. Please try again.");
              return;
            }

            // Get reCAPTCHA token
            const recaptchaToken = await getRecaptchaToken('verify_email');
            if (!recaptchaToken) {
              error("Security verification failed. Please try again.");
              return;
            }
            
            // Step 3: Verify OTP - Now properly passing recaptchaToken
            const verifyResponse = await verifyEmail({ 
              email, 
              otp, 
              expirationTime, 
              recaptchaToken: recaptchaToken
            }).unwrap();
            
            if (!verifyResponse.success) {
                setFormErrors((prevErrors) => ({
                    ...prevErrors,
                    otp: verifyResponse.message || 'Email verification failed.',
                }));
                setIsVerifyEmailLoading(false);
                return;
            }
    
            // Step 6: Clear form after successful verification
            setEmail('');
            setOTP('');
            // Redirect to /reset-password with email state
            navigate('/reset-password', { state: { email } });
        } catch (err) {
            const errorMessage = err?.data?.message || 'An unexpected error occurred.';
            setFormErrors((prevErrors) => ({
                ...prevErrors,
                otp: errorMessage,
            }));
            error(errorMessage);
        } finally {
            // Ensure loading state is reset
            setIsVerifyEmailLoading(false);
        }
    };

  return (
    <Flex
        minH={'100vh'}
        justify={'center'}      
        bg={bgColor}
        color={textColor}
        width="100%">
        <Stack
            w={'full'}
            maxW={'lg'}
            bg={bgColorDark}
            p={8}
            >
            <Image 
            src="/logo512.png" 
            alt="iFun Baby Logo" 
            boxSize="150px" 
            mx="auto" 
            mt={7}
            />
            <Text display='flex' justifyContent='center' alignItems='center' fontWeight={'bold'} fontSize={{ base: '2xl', sm: '3xl' }} mb="4">
                Reset password
            </Text>
            <Text display='flex' justifyContent='center' alignItems='center' fontSize={{ base: 'sm', sm: 'md' }} mb="4">
                Enter your email address and we will send you a verification code to reset your password.
            </Text>

            <FormControl isRequired isInvalid={!!formErrors?.email}>
                <InputGroup>
                <InputLeftElement>
                    <FontAwesomeIcon
                    color='gray'
                    icon={faAt}
                    />
                </InputLeftElement>
                <Input 
                    id="email"
                    type="email" 
                    value={email}
                    placeholder='Email address'
                    autoComplete='false'
                    onChange={handleEmailChange} 
                    onBlur={handleEmailBlur}
                    />
                </InputGroup>
                <FormErrorMessage>{formErrors?.email}</FormErrorMessage>
            </FormControl>

            <Stack
                direction={{ base: 'column', sm: 'row' }}
                align={'start'}
                justify={'space-between'}
            >
                <FormControl isRequired isInvalid={!!formErrors?.otp}>
                <Input
                    id="otp"
                    variant="outline"
                    type="text"
                    value={otp}
                    onChange={handleOTPChange}
                    placeholder="# Code"
                    width={{ base: '100%', sm: '95%' }}
                />
                {/* <FormErrorMessage>{formErrors?.otp}</FormErrorMessage> */}
                </FormControl>

                <Button
                    variant={'outline'}
                    onClick={sendOTP}
                    colorScheme="teal"
                    width={{ base: '100%', sm: '40%' }}
                    isDisabled={resendDisabled}
                    isLoading={isSendOtpLoading}
                >
                {resendDisabled ? `Resend in ${formatTime(resendTimer)}` : "Send code"}
                </Button>
            </Stack>

            <Stack spacing={10} pt={2}>
                <Button
                colorScheme='teal'
                onClick={submitHandler} 
                isLoading={isVerifyEmailLoading}>
                Continue
                </Button>
            </Stack>
            <Stack
                direction="row"           
                width="100%"              
                alignItems={'flex-start'}
                justifyContent={'space-between'}
                flexWrap="nowrap"
                >
                <Text>
                
                </Text>
                <Link color="#1A73E8" href='/login'>
                Back to log in
                </Link>
            </Stack>
            </Stack>
    </Flex>
  );
};

export default UserForgetPassword;
